import type { DocNode } from '@atlaskit/adf-schema';

/**
 * Type guard to check if a parsed object is a valid ADF DocNode
 *
 * Performs basic structural validation of ADF documents. This intentionally does not validate
 * the entire ADF content tree, as ReactRenderer will handle that validation during rendering.
 * This approach allows for graceful degradation - if the ADF is invalid at a deeper level,
 * the component will fallback to plain text rendering.
 *
 * @param {unknown} obj - Object to validate
 * @returns {boolean} True if the object has the basic structure of an ADF DocNode
 */
const isValidADFDocument = (obj: unknown): obj is DocNode => {
	if (!obj || typeof obj !== 'object') {
		return false;
	}

	const doc = obj as Partial<DocNode>;

	// ADF documents must have type 'doc', version 1, and content array
	// Note: We don't validate the content array items themselves, as ReactRenderer
	// will handle that and provide appropriate error handling
	return doc.type === 'doc' && doc.version === 1 && Array.isArray(doc.content);
};

/**
 * Safely parses guideline ADF JSON string with validation
 *
 * @param {string | undefined} guideline - JSON string to parse
 * @returns {DocNode | undefined} Parsed and validated ADF document or undefined if parsing/validation fails
 *
 * Note: This function intentionally returns undefined on errors to allow graceful fallback to plain text.
 * In development mode, errors are logged to the console for debugging purposes.
 */
export const safeParseGuidelineADF = (guideline?: string): DocNode | undefined => {
	if (!guideline) {
		return undefined;
	}

	try {
		const parsed = JSON.parse(guideline);

		if (!isValidADFDocument(parsed)) {
			return undefined;
		}

		return parsed;
	} catch (error) {
		return undefined;
	}
};
